/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  CacheEvictor,
  ChallengerCacheEviction,
  assertUnreachable,
  canonicalizeBaseUrl,
  getGlobalLogLevel,
  setGlobalLogLevelFromString,
} from "@gnu-taler/taler-util";
import {
  BrowserHashNavigationProvider,
  ChallengerApiProvider,
  type ChallengerApiProviderStatus,
  NotificationProvider,
  TalerWalletIntegrationBrowserProvider,
  TranslationProvider,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { SWRConfig } from "swr";
import { Routing } from "./Routing.js";
import { SettingsProvider } from "./context/settings.js";
import { revalidateChallengeSession } from "./hooks/challenge.js";
import { strings } from "./i18n/strings.js";
import {
  ActionButton,
  formatErrorDetails,
  InlineNotice,
  LoadingState,
  TechnicalDetails,
  VerificationCard,
} from "./components/VerificationUi.js";
import { Frame } from "./pages/Frame.js";
import { ChallengerUiSettings, fetchSettings } from "./settings.js";

const WITH_LOCAL_STORAGE_CACHE = false;

const evictBankSwrCache: CacheEvictor<ChallengerCacheEviction> = {
  async notifySuccess(op) {
    switch (op) {
      case ChallengerCacheEviction.CREATE_CHALLENGE: {
        await Promise.all([revalidateChallengeSession()]);
        return;
      }
      case ChallengerCacheEviction.SOLVE_CHALLENGE: {
        await Promise.all([revalidateChallengeSession()]);
        return;
      }
      default: {
        assertUnreachable(op);
      }
    }
  },
};

export function App(): VNode {
  return (
    <TranslationProvider source={strings}>
      <ConfiguredApp />
    </TranslationProvider>
  );
}

function ConfiguredApp(): VNode {
  const { i18n } = useTranslationContext();
  const [settings, setSettings] = useState<ChallengerUiSettings>();
  useEffect(() => {
    fetchSettings(setSettings);
  }, []);
  if (!settings) {
    // Translators: Initial full-page loading title while the web UI reads its
    // local configuration, before contacting Challenger.
    const preparingTitle = i18n.str`Preparing verification…`;
    return (
      <div class="flex min-h-screen items-center justify-center bg-secondaryContainer px-4 dark:bg-darkBackground">
        <LoadingState title={preparingTitle} />
      </div>
    );
  }

  const baseUrl = getInitialBackendBaseURL(settings.backendBaseURL);
  return (
    <SettingsProvider value={settings}>
      <NotificationProvider>
        <ChallengerApiProvider
          baseUrl={new URL(baseUrl)}
          frameOnError={Frame}
          renderStatus={(status) => (
            <ChallengerConnectionStatus status={status} />
          )}
          evictors={{
            challenger: evictBankSwrCache,
          }}
        >
          <SWRConfig
            value={{
              provider: WITH_LOCAL_STORAGE_CACHE
                ? localStorageProvider
                : undefined,
              // normally, do not revalidate
              revalidateOnFocus: false,
              revalidateOnReconnect: true,
              revalidateIfStale: false,
              revalidateOnMount: undefined,
              focusThrottleInterval: undefined,

              // normally, do not refresh
              refreshInterval: undefined,
              dedupingInterval: 2000,
              refreshWhenHidden: false,
              refreshWhenOffline: false,

              // ignore errors
              shouldRetryOnError: false,
              errorRetryCount: 0,
              errorRetryInterval: undefined,

              // do not go to loading again if already has data
              keepPreviousData: false,
            }}
          >
            <TalerWalletIntegrationBrowserProvider>
              <BrowserHashNavigationProvider>
                <Routing />
              </BrowserHashNavigationProvider>
            </TalerWalletIntegrationBrowserProvider>
          </SWRConfig>
        </ChallengerApiProvider>
      </NotificationProvider>
    </SettingsProvider>
  );
}

function ChallengerConnectionStatus({
  status,
}: {
  status: ChallengerApiProviderStatus;
}): VNode {
  const { i18n } = useTranslationContext();
  if (status.type === "loading") {
    // Translators: %1$s is the current automatic connection retry; %2$s is
    // the maximum number of automatic retries.
    const retryDescription = i18n.str`Retry ${status.attempt} of ${status.maxAttempts}`;
    // Translators: Loading title shown during an automatic reconnection attempt
    // after Challenger could not be reached.
    const reconnectingTitle = i18n.str`Reconnecting to the verification service…`;
    // Translators: Loading title for the first connection from this web page to
    // the Challenger service.
    const connectingTitle = i18n.str`Connecting to the verification service…`;
    return (
      <LoadingState
        title={status.attempt ? reconnectingTitle : connectingTitle}
        description={status.attempt ? retryDescription : undefined}
      />
    );
  }
  if (status.type === "incompatible") {
    // Translators: Fallback adjective for an unavailable service protocol
    // version in the technical details.
    const unknownVersion = i18n.str`unknown`;
    // Translators: %1$s is the web page's supported Challenger protocol
    // version; %2$s is the service's protocol version or "unknown".
    const versionDetails = i18n.str`Supported version: ${status.supported}. Service version: ${status.serverVersion ?? unknownVersion}.`;
    // Translators: Page title indicating that this web UI and Challenger use
    // incompatible protocol versions and one must be updated.
    const updateRequiredTitle = i18n.str`Verification service update required`;
    // Translators: Error-notice heading for incompatible web UI and Challenger
    // protocol versions.
    const incompatibleTitle = i18n.str`Versions are incompatible`;
    // Translators: Explanation shown when incompatible protocol versions prevent
    // this page from continuing the verification flow.
    const incompatibleDescription = i18n.str`This verification page cannot communicate with the service. Please return to the application that sent you here.`;
    return (
      <VerificationCard title={updateRequiredTitle}>
        <InlineNotice tone="error" title={incompatibleTitle}>
          {incompatibleDescription}
        </InlineNotice>
        <TechnicalDetails copyText={versionDetails}>
          {versionDetails}
        </TechnicalDetails>
      </VerificationCard>
    );
  }
  // Translators: Page title shown when all attempts to connect to Challenger
  // have failed.
  const unavailableTitle = i18n.str`Verification service unavailable`;
  // Translators: Short error-notice heading for a failed Challenger connection.
  const connectionFailureTitle = i18n.str`We could not connect`;
  // Translators: Reassures the user that a connection failure did not modify
  // their verification data.
  const connectionFailureDescription = i18n.str`Check your connection and try again. Your verification information has not been changed.`;
  // Translators: Busy-state label on the manual reconnect button.
  const connectingLabel = i18n.str`Connecting…`;
  // Translators: Button that manually retries the connection to Challenger.
  const retryLabel = i18n.str`Try again`;
  const technicalDetails = formatErrorDetails(status.error);
  return (
    <VerificationCard title={unavailableTitle}>
      <InlineNotice tone="error" title={connectionFailureTitle}>
        {connectionFailureDescription}
      </InlineNotice>
      <div class="mt-5">
        <ActionButton busyLabel={connectingLabel} onClick={status.retry}>
          {retryLabel}
        </ActionButton>
      </div>
      <TechnicalDetails copyText={technicalDetails}>
        {technicalDetails}
      </TechnicalDetails>
    </VerificationCard>
  );
}

// @ts-expect-error creating a new property for window object
window.setGlobalLogLevelFromString = setGlobalLogLevelFromString;
// @ts-expect-error creating a new property for window object
window.getGlobalLevel = getGlobalLogLevel;

function localStorageProvider(): Map<unknown, unknown> {
  const map = new Map(JSON.parse(localStorage.getItem("app-cache") || "[]"));

  window.addEventListener("beforeunload", () => {
    const appCache = JSON.stringify(Array.from(map.entries()));
    localStorage.setItem("app-cache", appCache);
  });
  return map;
}

export function getInitialBackendBaseURL(
  backendFromSettings: string | undefined,
): string {
  const overrideUrl =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("challenger-base-url")
      : undefined;
  let result: string;

  if (!overrideUrl) {
    // normal path
    if (!backendFromSettings) {
      console.error(
        "ERROR: backendBaseURL was overridden by a setting file and missing. Setting value to 'window.origin'",
      );
      result = window.origin;
    } else {
      result = backendFromSettings;
    }
  } else {
    // testing/development path
    result = overrideUrl;
  }
  try {
    return canonicalizeBaseUrl(result);
  } catch {
    // fall back
    return canonicalizeBaseUrl(window.origin);
  }
}
